import { TaskFieldsTableLayout } from "@jugl-web/domain-resources/tasks/components/TaskFieldsTableLayout";

import {
  convertSnakeCaseToReadableString as humanizeName,
  useTranslations,
} from "@jugl-web/utils";
import { FC, useCallback, useMemo, useState } from "react";
import { useRestApiProvider, FormField } from "@jugl-web/rest-api";
import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";
import { useDndMonitor } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import debounce from "lodash/debounce";
import {
  alwaysVisibleCompanyFields,
  alwaysVisiblePersonalFields,
} from "../consts";
import { CustomerFieldVisibilityConfirmAlert } from "./CustomerFieldVisibilityConfirmAlert";
import { CustomerFieldRow } from "./CustomerFieldRow";

const { EmptyState } = TaskFieldsTableLayout;

interface CustomerFieldsTableProps {
  entityId: string;
  searchQuery: string;
  variant: FormField["section"];
  openManageFieldSidebar: (field: FormField) => void;
}

export const CustomerFieldRows: FC<CustomerFieldsTableProps> = ({
  entityId,
  searchQuery,
  variant,
  openManageFieldSidebar,
}) => {
  const { t } = useTranslations();
  const [optimisticRequired, setOptimisticRequired] = useState<
    Record<string, boolean>
  >({});
  const [optimisticVisibility, setOptimisticVisibility] = useState<
    Record<string, boolean>
  >({});
  const { customersFormFieldApi } = useRestApiProvider();
  const { formFields } = useCustomerFields({
    entityId,
  });

  const [optimisticSort, setOptimisticSort] = useState<Record<string, number>>(
    {}
  );

  const [editFormField] = customersFormFieldApi.useEditFormFieldMutation();
  const [batchEditFormFields] =
    customersFormFieldApi.useBatchEditFormFieldsMutation();
  const [
    confirmVisibilityChangeAlertState,
    setConfirmVisibilityChangeAlertState,
  ] = useState({
    isOpen: false,
    field: null as FormField | null,
  });

  const transformedFormFields = useMemo(() => {
    return formFields
      ?.filter((field) => field.section === variant)
      .sort((a, b) => {
        const posA = optimisticSort[a.id] ?? a.pos ?? 0;
        const posB = optimisticSort[b.id] ?? b.pos ?? 0;
        return posA - posB;
      });
  }, [formFields, variant, optimisticSort]);

  const handleFieldUpdate = useCallback(
    async (
      field: FormField,
      key: "required" | "visibility",
      setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
    ) => {
      setter((prev) => ({
        ...prev,
        [field.id]: !(prev[field.id] !== undefined
          ? prev[field.id]
          : field[key]),
      }));

      const currentValue =
        key === "required"
          ? optimisticRequired[field.id] !== undefined
            ? optimisticRequired[field.id]
            : field.required
          : optimisticVisibility[field.id] !== undefined
            ? optimisticVisibility[field.id]
            : field.visibility;
      const response = await editFormField({
        entityId,
        fieldId: field.id,
        params: {
          [key]: !currentValue,
        },
        meta: {
          successMessage: t({
            id: "feedback.field-updated",
            defaultMessage: "Field has been updated",
          }),
        },
      });

      if (!("data" in response)) {
        setter((prev) => ({ ...prev, [field.id]: field[key] }));
      }
    },
    [editFormField, entityId, optimisticRequired, optimisticVisibility, t]
  );

  const handleMandatoryCheckboxClick = useCallback(
    (e: React.SyntheticEvent, field: FormField) => {
      e.stopPropagation();
      handleFieldUpdate(field, "required", setOptimisticRequired);
    },
    [handleFieldUpdate]
  );

  const handleVisibilityChange = useCallback(
    (field: FormField) => {
      handleFieldUpdate(field, "visibility", setOptimisticVisibility);
    },
    [handleFieldUpdate]
  );

  useDndMonitor({
    onDragEnd: async (event) => {
      const { active, over } = event;
      const nonDraggableFieldNames = ["first_name", "last_name"];
      const nonDraggableFieldIds = formFields
        .filter((field) => nonDraggableFieldNames.includes(field.name))
        .map((field) => field.id);
      if (
        nonDraggableFieldIds.includes(active.id as string) ||
        (over && nonDraggableFieldIds.includes(over.id as string))
      ) {
        return;
      }

      if (over && active.id !== over.id) {
        const oldIndex = transformedFormFields.findIndex(
          (f) => f.id === active.id
        );
        const newIndex = transformedFormFields.findIndex(
          (f) => f.id === over.id
        );

        const reorderedFields = arrayMove(
          transformedFormFields,
          oldIndex,
          newIndex
        );

        const updatedFieldsPayload = reorderedFields.map(
          (field: FormField, index: number) => ({
            id: field.id,
            pos: index + 1,
          })
        );
        setOptimisticSort(
          updatedFieldsPayload.reduce(
            (acc, field) => {
              acc[field.id] = field.pos;
              return acc;
            },
            {} as Record<string, number>
          )
        );
        const response = await batchEditFormFields({
          entityId,
          params: {
            fields: updatedFieldsPayload,
          },
          meta: {
            successMessage: t({
              id: "feedback.fields-reordered",
              defaultMessage: "Fields have been reordered",
            }),
          },
        });

        if ("error" in response) {
          setOptimisticSort({});
        }
      }
    },
  });

  const handleVisibilityFlowClick = useCallback(
    (field: FormField) => {
      const visibilityState =
        optimisticVisibility[field.id] !== undefined
          ? optimisticVisibility[field.id]
          : field.visibility;
      if (visibilityState) {
        setConfirmVisibilityChangeAlertState({
          isOpen: true,
          field,
        });
      } else {
        handleVisibilityChange(field);
      }
    },
    [handleVisibilityChange, optimisticVisibility]
  );

  const debouncedOnMandatoryClick = useMemo(
    () => debounce(handleMandatoryCheckboxClick, 450),
    [handleMandatoryCheckboxClick]
  );
  const debouncedOVisibilityClick = useMemo(
    () => debounce(handleVisibilityFlowClick, 450),
    [handleVisibilityFlowClick]
  );

  return (
    <>
      <CustomerFieldVisibilityConfirmAlert
        isOpen={confirmVisibilityChangeAlertState.isOpen}
        fieldName={confirmVisibilityChangeAlertState.field?.name || ""}
        onClose={() =>
          setConfirmVisibilityChangeAlertState({
            isOpen: false,
            field: null,
          })
        }
        onConfirm={() => {
          if (confirmVisibilityChangeAlertState.field) {
            handleVisibilityChange(confirmVisibilityChangeAlertState.field);
          }
        }}
      />
      {transformedFormFields?.length ? (
        <SortableContext
          items={transformedFormFields}
          strategy={verticalListSortingStrategy}
        >
          {transformedFormFields.map((field) => {
            if (
              searchQuery &&
              !humanizeName(field.name)
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
            ) {
              return null;
            }

            const isAlwaysVisible =
              variant === "personal"
                ? alwaysVisiblePersonalFields.includes(field.name)
                : alwaysVisibleCompanyFields.includes(field.name);
            const visibilityState =
              optimisticVisibility[field.id] === undefined
                ? field.visibility
                : optimisticVisibility[field.id];
            const mandatoryState =
              optimisticRequired[field.id] === undefined
                ? field.required
                : optimisticRequired[field.id];
            return (
              <CustomerFieldRow
                key={field.id}
                field={field}
                entityId={entityId}
                searchQuery={searchQuery}
                variant={variant}
                isVisible={visibilityState}
                isMandatory={mandatoryState}
                openManageFieldSidebar={() => openManageFieldSidebar(field)}
                onMandatoryCheckboxClick={(e, formField) =>
                  debouncedOnMandatoryClick(e, formField)
                }
                onVisibilityClick={(e) => {
                  e.stopPropagation();
                  if (isAlwaysVisible) {
                    return;
                  }
                  debouncedOVisibilityClick(field);
                }}
              />
            );
          })}
        </SortableContext>
      ) : (
        <EmptyState />
      )}
    </>
  );
};
